import { useEffect, useRef, useState, type FormEvent } from "react";
import { Eye, EyeOff, RefreshCw, Save, ShieldCheck } from "lucide-react";
import type { MindsetIntegrationHostAccessPolicy } from "@mindset/api-client";
import type { IntegrationHostAccessClient } from "./integrationSetupClient";

interface Props {
  companyId: string; userId: string; applicationId: string; client: IntegrationHostAccessClient; onChanged?: () => void;
}
export function IntegrationHostAccess(props: Props) {
  return <HostAccessForm key={`${props.companyId}:${props.userId}:${props.applicationId}`} {...props} />;
}
function HostAccessForm({ companyId, userId, applicationId, client, onChanged }: Props) {
  const [policy, setPolicy] = useState<MindsetIntegrationHostAccessPolicy | null>(null);
  const [endpoint, setEndpoint] = useState("");
  const [secret, setSecret] = useState("");
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failedRecovery, setFailedRecovery] = useState(false);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const [now, setNow] = useState(Date.now);
  const alive = useRef(false), running = useRef(false), requestKey = useRef<string | null>(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const recover = client.currentHostPolicy;
  useEffect(() => {
    let current = true;
    setLoading(true); setFailedRecovery(false); setError("");
    void recover(companyId, userId, applicationId).then((result) => {
      if (!current) return;
      setPolicy(result); setEndpoint(result?.endpoint ?? ""); setNow(Date.now());
    }, () => { if (current) { setFailedRecovery(true); setError("Saved callback settings could not be loaded."); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [recover, companyId, userId, applicationId, version]);
  useEffect(() => {
    if (!policy?.verifiedUntil) return;
    const remaining = Date.parse(policy.verifiedUntil) - Date.now();
    if (remaining <= 0) { setNow(Date.now()); return; }
    const timer = setTimeout(() => setNow(Date.now()), Math.min(remaining + 1, 2147483647));
    return () => clearTimeout(timer);
  }, [policy]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (running.current || loading || failedRecovery) return;
    running.current = true; setBusy(true); setError("");
    requestKey.current ??= crypto.randomUUID();
    try {
      const saved = await client.registerHostPolicy(companyId, userId, applicationId, requestKey.current,
        { endpoint, apiKey: secret, expectedVersion: policy?.version ?? 0 });
      if (!alive.current) return;
      setPolicy(saved); setEndpoint(saved.endpoint); setSecret(""); setReveal(false); requestKey.current = null; onChanged?.();
    } catch { if (alive.current) setError("Save was not confirmed. Retry or refresh the saved settings."); }
    finally { running.current = false; if (alive.current) setBusy(false); }
  }
  async function verify() {
    if (!policy || running.current || loading || failedRecovery || requestKey.current || endpoint !== policy.endpoint || secret) return;
    running.current = true; setBusy(true); setError("");
    try {
      await client.verifyHostPolicy(companyId, userId, policy.id);
      const saved = await recover(companyId, userId, applicationId);
      if (alive.current) { setPolicy(saved); setNow(Date.now()); onChanged?.(); }
    } catch { if (alive.current) setError("Callback verification was not confirmed. Check domain verification and callback settings."); }
    finally { running.current = false; if (alive.current) setBusy(false); }
  }
  const verified = policy?.state === "VERIFIED" && Boolean(policy.verifiedUntil) && Date.parse(policy.verifiedUntil!) > now;
  const dirty = Boolean(policy && (endpoint !== policy.endpoint || secret));
  const locked = loading || busy || failedRecovery || Boolean(requestKey.current);
  return <section className="integration-domain integration-host-access" aria-label="Application access callback">
    <header><strong>Access callback</strong><span role="status">{loading ? "Loading..." : failedRecovery ? "Status unavailable" : dirty ? "Unsaved changes" : verified ? "Verified" : policy?.state === "REVOKED" ? "Revoked" : policy?.state === "VERIFIED" ? "Verification expired" : policy ? "Awaiting verification" : "Not configured"}</span></header>
    <form onSubmit={(event) => void save(event)}>
      <label>Callback URL<input type="url" required value={endpoint} disabled={locked} onChange={(event) => setEndpoint(event.target.value)} autoComplete="off" /></label>
      <label>Callback key<div className="integration-host-secret"><input aria-label="Callback key" type={reveal ? "text" : "password"} required minLength={43} maxLength={128} pattern="[A-Za-z0-9_-]{43,128}" value={secret} disabled={locked} onChange={(event) => setSecret(event.target.value)} autoComplete="new-password" spellCheck={false} />
        <button type="button" className="portal-icon-button" aria-label={reveal ? "Hide callback key" : "Show callback key"} title={reveal ? "Hide callback key" : "Show callback key"} onClick={() => setReveal(!reveal)}>{reveal ? <EyeOff size={16} /> : <Eye size={16} />}</button></div></label>
      <div className="integration-actions">
        <button type="submit" className="portal-button" disabled={loading || busy || failedRecovery || !secret || !endpoint}><Save size={16} />{requestKey.current ? "Retry save" : policy ? "Replace callback" : "Save callback"}</button>
        <button type="button" className="portal-button" disabled={locked || dirty || !policy || policy.state === "REVOKED" || verified} onClick={() => void verify()}><ShieldCheck size={16} />Verify callback</button>
        <button type="button" className="portal-icon-button" title="Refresh callback" aria-label="Refresh callback" disabled={loading || busy} onClick={() => { setSecret(""); setReveal(false); requestKey.current = null; setVersion((value) => value + 1); }}><RefreshCw size={16} /></button>
      </div>
    </form>
    {policy?.verifiedUntil ? <p>Verification expires <time dateTime={policy.verifiedUntil}>{new Date(policy.verifiedUntil).toLocaleString()}</time></p> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
